import { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Gamepad2, ArrowUpDown } from 'lucide-react'
import type { ProviderStatsData } from '@/modules/manage-player/type'

export const providerStatsColumns: ColumnDef<ProviderStatsData>[] = [
	{
		accessorKey: 'provider',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="-ml-4"
				>
					ค่ายเกม
					<ArrowUpDown className="ml-2 h-4 w-4" />
				</Button>
			)
		},
		cell: ({ row }) => (
			<div className="flex items-center gap-2">
				<Gamepad2 className="h-4 w-4 text-primary" />
				<span className="font-semibold uppercase">{row.original.provider}</span>
			</div>
		),
	},
	{
		accessorKey: 'totalTransferIn',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="-ml-4"
				>
					โอนเข้า
					<ArrowUpDown className="ml-2 h-4 w-4" />
				</Button>
			)
		},
		cell: ({ row }) => (
			<span className="font-medium text-green-600">
				{row.original.totalTransferIn.toLocaleString('th-TH', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}
			</span>
		),
	},
	{
		accessorKey: 'totalTransferOut',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="-ml-4"
				>
					โอนออก
					<ArrowUpDown className="ml-2 h-4 w-4" />
				</Button>
			)
		},
		cell: ({ row }) => (
			<span className="font-medium text-red-600">
				{row.original.totalTransferOut.toLocaleString('th-TH', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}
			</span>
		),
	},
	{
		accessorKey: 'totalBetAmount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="-ml-4"
				>
					ยอดเดิมพัน
					<ArrowUpDown className="ml-2 h-4 w-4" />
				</Button>
			)
		},
		cell: ({ row }) => (
			<span className="font-medium text-blue-600">
				{row.original.totalBetAmount.toLocaleString('th-TH', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}
			</span>
		),
	},
	{
		accessorKey: 'totalTurnover',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="-ml-4"
				>
					เทิร์นโอเวอร์
					<ArrowUpDown className="ml-2 h-4 w-4" />
				</Button>
			)
		},
		cell: ({ row }) => (
			<span className="font-medium text-yellow-600">
				{row.original.totalTurnover.toLocaleString('th-TH', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}
			</span>
		),
	},
	{
		accessorKey: 'totalProfit',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="-ml-4"
				>
					กำไร/ขาดทุน
					<ArrowUpDown className="ml-2 h-4 w-4" />
				</Button>
			)
		},
		cell: ({ row }) => {
			const profit = row.original.totalProfit
			const isProfit = profit >= 0
			return (
				<Badge
					variant="outline"
					className={
						isProfit
							? 'border-green-200 bg-green-50 text-green-700'
							: 'border-red-200 bg-red-50 text-red-700'
					}
				>
					{isProfit ? '+' : ''}
					{profit.toLocaleString('th-TH', {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})}
				</Badge>
			)
		},
	},
	{
		accessorKey: 'totalPlayCount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="-ml-4"
				>
					จำนวนเกม
					<ArrowUpDown className="ml-2 h-4 w-4" />
				</Button>
			)
		},
		cell: ({ row }) => (
			<span className="font-medium">
				{row.original.totalPlayCount.toLocaleString('th-TH')}
			</span>
		),
	},
	{
		id: 'playTime',
		header: 'เวลาเล่น',
		cell: ({ row }) => {
			const firstPlay = row.original.firstPlayAt
			const lastPlay = row.original.lastPlayAt

			if (!firstPlay && !lastPlay)
				return <span className="text-muted-foreground">-</span>

			return (
				<div className="space-y-1 text-xs">
					{firstPlay && typeof firstPlay === 'string' && (
						<div>
							<span className="text-muted-foreground">เริ่ม: </span>
							{format(new Date(firstPlay), 'dd/MM/yy HH:mm', { locale: th })}
						</div>
					)}
					{lastPlay && typeof lastPlay === 'string' && (
						<div>
							<span className="text-muted-foreground">ล่าสุด: </span>
							{format(new Date(lastPlay), 'dd/MM/yy HH:mm', { locale: th })}
						</div>
					)}
				</div>
			)
		},
	},
]
